import React, { useCallback, useEffect, useState } from 'react';
import {
  listIncidents, getIncident, createIncident, updateIncident, deleteIncident, addIncidentNote,
  listEvidence, createEvidence, deleteEvidence, appealTemplate, openHtml,
} from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import ModuleNav from '../components/Modules/ModuleNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import { SevBadge } from '../components/Security/Charts';
import RequirePermission from '../auth/RequirePermission';

const STATUSES = ['Open', 'Investigating', 'Mitigated', 'Closed'];
const SEVS = ['Low', 'Medium', 'High', 'Critical'];
const blank = { title: '', description: '', severity: 'Medium', status: 'Open', assignee: '', siteKey: '' };

export default function IncidentsPage() {
  const { sites, siteId, setSiteId } = useActiveSite();
  const [tab, setTab] = useState('incidents');
  const [filter, setFilter] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null); // {incident, notes, threats}
  const [form, setForm] = useState(null);
  const [note, setNote] = useState('');
  const [threatId, setThreatId] = useState('');
  const [evidence, setEvidence] = useState([]);
  const [evForm, setEvForm] = useState({ title: '', days: 14 });
  const [appeal, setAppeal] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    try { const [i, e] = await Promise.all([listIncidents(filter), listEvidence()]); setItems(i); setEvidence(e); }
    catch (e) { setError(errorMessage(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setError(''); setInfo('');
    try { const r = await fn(); if (ok) setInfo(ok); await load(); return r; } catch (e) { setError(errorMessage(e)); return null; }
  };
  const openIncident = async (id) => { try { setOpen(await getIncident(id)); } catch (e) { setError(errorMessage(e)); } };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const r = await act(() => (form.id ? updateIncident(form.id, form) : createIncident({ ...form, siteKey: form.siteKey || siteId || null })), 'Incident saved.');
    if (r) { setForm(null); openIncident(r.id); }
  };

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Incidents & evidence</h1>
        <p className="muted">Keep a record of attacks, gather evidence and prepare an appeal for Google.</p></div></header>
      <ModuleNav />
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}
      <div className="mod-tabs">
        {[['incidents', 'Incidents'], ['evidence', 'Evidence packages'], ['appeal', 'Appeal helper']].map(([k, l]) =>
          <button key={k} className={tab === k ? 'primary' : 'ghost'} onClick={() => setTab(k)}>{l}</button>)}
      </div>

      {tab === 'incidents' && !open && (
        <>
          <div className="sec-toolbar">
            <select className="sec-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
            <RequirePermission code="workspace.write"><button className="primary" onClick={() => setForm({ ...blank })}>+ New incident</button></RequirePermission>
          </div>
          <div className="sec-card sec-scroll">
            <table className="sec-table">
              <thead><tr><th>Title</th><th>Severity</th><th>Status</th><th>Assignee</th><th>Updated</th></tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="clickable" onClick={() => openIncident(i.id)}>
                    <td><b>{i.title}</b></td><td><SevBadge value={i.severity} /></td><td>{i.status}</td><td>{i.assignee || '—'}</td>
                    <td>{new Date(i.updatedAt).toLocaleString()}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan="5" className="muted">No incidents yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'incidents' && open && (
        <div className="mod-two">
          <div className="sec-card">
            <button className="ghost" onClick={() => setOpen(null)}>← Back</button>
            <h2 style={{ margin: '12px 0 4px' }}>{open.incident.title}</h2>
            <div className="sec-row"><SevBadge value={open.incident.severity} /><span className="mod-chip">{open.incident.status}</span>
              {open.incident.assignee && <span className="mod-chip">Assigned: {open.incident.assignee}</span>}</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>{open.incident.description || <span className="muted">No description.</span>}</p>
            <RequirePermission code="workspace.write">
              <div className="sec-row">
                <select className="sec-select" value={open.incident.status}
                  onChange={(e) => act(async () => { const r = await updateIncident(open.incident.id, { ...open.incident, status: e.target.value }); await openIncident(r.id); })}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                <button className="ghost" onClick={() => setForm({ ...open.incident, siteKey: open.incident.siteKey || '' })}>Edit</button>
                <button className="ghost danger" onClick={() => window.confirm('Delete this incident and its notes?') && act(async () => { await deleteIncident(open.incident.id); setOpen(null); })}>Delete</button>
              </div>
            </RequirePermission>
          </div>
          <div className="sec-card">
            <h3>Timeline</h3>
            {open.notes.map((n) => (
              <div key={n.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span className="muted" style={{ fontSize: 12 }}>{new Date(n.createdAt).toLocaleString()} · {n.kind}</span>
                <div>{n.body}{n.threatId && <> — threat <span className="sec-mono">{n.threatId.slice(0, 8)}</span></>}</div>
              </div>
            ))}
            {open.threats.length > 0 && (
              <>
                <h3 style={{ marginTop: 16 }}>Linked threats</h3>
                <table className="sec-table"><thead><tr><th>IP</th><th>Score</th><th>Action</th><th>Reasons</th></tr></thead>
                  <tbody>{open.threats.map((t) => <tr key={t.id}><td className="sec-mono">{t.ipAddress}</td><td>{t.riskScore}</td><td>{t.actionTaken}</td><td>{t.reasonCodes}</td></tr>)}</tbody></table>
              </>
            )}
            <RequirePermission code="workspace.write">
              <form className="mod-form" style={{ marginTop: 14 }} onSubmit={(e) => {
                e.preventDefault();
                act(async () => { await addIncidentNote(open.incident.id, { body: note, threatId: threatId || null }); setNote(''); setThreatId(''); await openIncident(open.incident.id); });
              }}>
                <textarea className="sec-input" rows="3" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} />
                <input className="sec-input" placeholder="Optional: link a threat by its ID" value={threatId} onChange={(e) => setThreatId(e.target.value)} />
                <button className="primary" type="submit" disabled={!note && !threatId}>Add to timeline</button>
              </form>
            </RequirePermission>
          </div>
        </div>
      )}

      {form && (
        <div className="sec-drawer-bg" onClick={() => setForm(null)}>
          <form className="sec-drawer mod-form" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <h3>{form.id ? 'Edit incident' : 'New incident'}</h3>
            <label>Title<input className="sec-input" required maxLength={200} value={form.title} onChange={set('title')} /></label>
            <label>Description<textarea className="sec-input" rows="4" value={form.description} onChange={set('description')} /></label>
            <label>Severity<select className="sec-select" value={form.severity} onChange={set('severity')}>{SEVS.map((s) => <option key={s}>{s}</option>)}</select></label>
            {form.id && <label>Status<select className="sec-select" value={form.status} onChange={set('status')}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>}
            <label>Assignee<input className="sec-input" value={form.assignee} onChange={set('assignee')} /></label>
            {!form.id && <label>Site<SiteSelector sites={sites} value={form.siteKey || siteId} onChange={(v) => { setSiteId(v); setForm({ ...form, siteKey: v }); }} /></label>}
            <div className="sec-row"><button className="primary" type="submit">Save</button><button className="ghost" type="button" onClick={() => setForm(null)}>Cancel</button></div>
          </form>
        </div>
      )}

      {tab === 'evidence' && (
        <>
          <RequirePermission code="workspace.write">
            <form className="sec-card mod-form" style={{ marginBottom: 16 }} onSubmit={(e) => {
              e.preventDefault();
              act(() => createEvidence({ title: evForm.title, days: Number(evForm.days), siteKey: siteId || null }), 'Evidence package created.');
            }}>
              <h3>New evidence package</h3>
              <label>Title<input className="sec-input" value={evForm.title} onChange={(e) => setEvForm({ ...evForm, title: e.target.value })} placeholder="e.g. Click spike from 12 March" /></label>
              <label>Site<SiteSelector sites={sites} value={siteId} onChange={setSiteId} /></label>
              <label>Days to include<input className="sec-input" type="number" min="1" max="90" value={evForm.days} onChange={(e) => setEvForm({ ...evForm, days: e.target.value })} /></label>
              <button className="primary" type="submit">Build package</button>
              <p className="mod-note">Captures executive numbers, offending IPs, network types, click quality, rapid clicks and device sharing as a printable page.</p>
            </form>
          </RequirePermission>
          <div className="sec-card sec-scroll">
            <table className="sec-table">
              <thead><tr><th>Title</th><th>Period</th><th>Created</th><th /></tr></thead>
              <tbody>
                {evidence.map((p) => (
                  <tr key={p.id}><td><b>{p.title}</b></td><td>{new Date(p.fromUtc).toLocaleDateString()} – {new Date(p.toUtc).toLocaleDateString()}</td><td>{new Date(p.createdAt).toLocaleString()}</td>
                    <td><div className="sec-row">
                      <button className="ghost" onClick={() => openHtml(`/incidents/evidence/${p.id}/html`).catch((e) => setError(errorMessage(e)))}>Open / print</button>
                      <RequirePermission code="workspace.write"><button className="ghost danger" onClick={() => window.confirm('Delete?') && act(() => deleteEvidence(p.id))}>Delete</button></RequirePermission>
                    </div></td></tr>
                ))}
                {evidence.length === 0 && <tr><td colSpan="4" className="muted">No packages yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'appeal' && (
        <div className="sec-card mod-form" style={{ maxWidth: 820 }}>
          <h3>Appeal helper</h3>
          <p className="muted">Generates a factual message you can adapt when contacting Google about invalid traffic. It cannot guarantee any outcome.</p>
          <div className="sec-row">
            <SiteSelector sites={sites} value={siteId} onChange={setSiteId} />
            <button className="primary" onClick={async () => { try { setAppeal((await appealTemplate({ siteKey: siteId, days: 30 })).text); } catch (e) { setError(errorMessage(e)); } }}>Generate draft</button>
          </div>
          {appeal && <>
            <textarea className="sec-input" rows="18" value={appeal} onChange={(e) => setAppeal(e.target.value)} />
            <button className="ghost" onClick={() => navigator.clipboard?.writeText(appeal)}>Copy</button>
          </>}
        </div>
      )}
    </div>
  );
}
